import { useMemo } from 'react';
import { Typography } from '@mui/material';

import { MINTSCAN_URL } from 'src/constants/explorer';
import { useCurrentAccount } from 'src/hooks/useAccount';

import {
  BodyContainer,
  BodyTextContainer,
  Container,
  GradientText,
  Header,
  HeaderTitle,
  IconContainer,
  RedirectButton,
  RightChevronIconContainer,
  StepContainer,
  StyledBottomSheet,
  StyledCircularProgress,
  SubTitleContainer,
  TxButton,
  URLButton,
} from './styled';
import IconButton from '../IconButton';
import StepItem from '../StepItem';

import { ReactComponent as CloseIcon } from 'src/assets/svg/Close.svg';
import { ReactComponent as RightChevronIcon } from 'src/assets/svg/RightChevron.svg';
import { ReactComponent as TxTrackingSuccessIcon } from 'src/assets/svg/TxSuccess.svg';
import { ReactComponent as TxTrackingFailedIcon } from 'src/assets/svg/TxTrackingFail.svg';

type TransactionBottomSheetProps = Omit<React.ComponentProps<typeof StyledBottomSheet>, 'children'> & {
  title: string;
  txHash?: string;
  currentStep?: number;
  isFailed?: boolean;
  isSuccess?: boolean;
  isBridge?: boolean;
  isStaking?: boolean;
  bodyText: string;
  subText?: string;
  redirectButtonText?: string;
  onClickRedirect?: () => void;
};

export default function TransactionBottomSheet({
  currentStep,
  bodyText,
  subText,
  title,
  isFailed,
  isBridge,
  isStaking,
  isSuccess,
  txHash,
  onClose,
  redirectButtonText,
  onClickRedirect,
  ...remainder
}: TransactionBottomSheetProps) {
  const { currentAccount } = useCurrentAccount();

  const stepItems = useMemo(() => {
    const steps = [
      { step: 1, title: 'Send to Bridge' },
      { step: 2, title: 'Receive wethDYDX' },
      { step: 3, title: 'Receive DYDX' },
    ];

    return currentStep
      ? steps.map((item) => {
          const isCurrent = item.step === currentStep;
          return {
            ...item,
            isCurrent,
            isCompleted: item.step < currentStep,
            isFailed: isFailed && isCurrent,
          };
        })
      : [];
  }, [currentStep, isFailed]);

  return (
    <StyledBottomSheet data-is-staking={isStaking} {...remainder} onClose={onClose}>
      <Container>
        <Header>
          <HeaderTitle>
            <Typography variant="h2">{title}</Typography>
          </HeaderTitle>
          <IconButton
            onClick={() => {
              onClose?.({}, 'backdropClick');
            }}
          >
            <CloseIcon />
          </IconButton>
        </Header>

        <StepContainer>
          {stepItems.map((item) => (
            <StepItem key={item.step} step={item.step} title={item.title} isCompleted={item.isCompleted} isCurrent={item.isCurrent} isFailed={item.isFailed} />
          ))}
        </StepContainer>
        <BodyContainer>
          {isSuccess && (
            <IconContainer>
              <TxTrackingSuccessIcon />
            </IconContainer>
          )}
          {isFailed && (
            <IconContainer>
              <TxTrackingFailedIcon />
            </IconContainer>
          )}

          {!(isSuccess || isFailed) && <StyledCircularProgress size={67} />}
          <BodyTextContainer>
            <Typography variant="h4">{bodyText}</Typography>
          </BodyTextContainer>

          {isSuccess && !isBridge && (
            <TxButton type="button" onClick={() => window.open(`${MINTSCAN_URL}/tx/${txHash || ''}` || '')}>
              <Typography variant="h7">{txHash}</Typography>
            </TxButton>
          )}

          {subText && (
            <SubTitleContainer>
              <Typography variant="h7">{subText}</Typography>
            </SubTitleContainer>
          )}

          {isSuccess && isBridge && (
            <SubTitleContainer>
              <Typography variant="h7">Bridge acknowledge transaction will appear on your dYdX Chain account in 30 minutes.</Typography>
              <URLButton type="button" onClick={() => window.open(`${MINTSCAN_URL}/address/${currentAccount.cosmosAddress}` || '')}>
                <Typography variant="h6">{currentAccount.cosmosAddress}</Typography>
              </URLButton>
              <Typography variant="h7">
                Native DYDX token minting on dYdX Chain will take up to 40 hours. Stake DYDX to help securing the network and start earning the staking rewards.
              </Typography>
            </SubTitleContainer>
          )}

          {redirectButtonText && (
            <RedirectButton>
              <GradientText>
                <Typography
                  variant="h4"
                  onClick={() => {
                    onClickRedirect?.();
                    onClose?.({}, 'backdropClick');
                  }}
                >
                  {redirectButtonText}
                </Typography>
              </GradientText>
              <RightChevronIconContainer>
                <RightChevronIcon />
              </RightChevronIconContainer>
            </RedirectButton>
          )}
        </BodyContainer>
      </Container>
    </StyledBottomSheet>
  );
}
